import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  EventType,
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';

import { App } from './App';
import { msalInstance } from './auth/msalConfig';
import './styles.css';

// Session-storage key holding the homeAccountId of the active account, so a
// fresh tab load can restore the same identity even when multiple accounts
// are cached. Lives in sessionStorage (same scope as MSAL's token cache) so
// closing the tab resets to "no preference" — matches the cacheLocation
// choice in msalConfig.
const ACTIVE_ACCOUNT_KEY = 'hub.activeAccountHomeId';

function rememberActiveAccount(account: AccountInfo | null): void {
  if (account) {
    sessionStorage.setItem(ACTIVE_ACCOUNT_KEY, account.homeAccountId);
  } else {
    sessionStorage.removeItem(ACTIVE_ACCOUNT_KEY);
  }
}

function setActiveAndRemember(account: AccountInfo): void {
  msalInstance.setActiveAccount(account);
  rememberActiveAccount(account);
}

// MSAL v3+ requires explicit init before any other API call, and
// handleRedirectPromise() MUST run on every page load (it parses the auth
// response hash if we just got redirected back from Entra). Doing both before
// React mounts means `<MsalAuthenticationTemplate>` sees the right state on
// first render — otherwise it would briefly think we're signed out and trigger
// a second redirect.
async function bootstrap(): Promise<void> {
  const rootElement = document.getElementById('root');
  if (rootElement === null) {
    throw new Error('Root container missing in index.html');
  }

  await msalInstance.initialize();

  // If we just landed on the redirect URI, this resolves with the account
  // info we need to mark active. On a normal page load it resolves to null.
  const result: AuthenticationResult | null = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    setActiveAndRemember(result.account);
  } else if (msalInstance.getActiveAccount() === null) {
    // Restore the previously-active account on fresh tab loads. Picking
    // accounts[0] blindly is wrong when the user has multiple cached
    // identities (e.g. personal MSA + corp work account) — the first one is
    // load-order-dependent and the redirect would resolve a different token
    // than the user expects.
    //
    // Strategy: remember the homeAccountId of whoever was active last and
    // restore THAT specific account. If the persisted id is no longer in the
    // cache (signed out elsewhere, cache evicted), fall back to auto-select
    // only when there's exactly one account; otherwise leave inactive so
    // MsalAuthenticationTemplate forces an interactive sign-in (which lets
    // the user pick).
    const accounts = msalInstance.getAllAccounts();
    const rememberedId = sessionStorage.getItem(ACTIVE_ACCOUNT_KEY);
    const remembered = rememberedId !== null
      ? accounts.find((a) => a.homeAccountId === rememberedId) ?? null
      : null;
    if (remembered) {
      msalInstance.setActiveAccount(remembered);
    } else if (accounts.length === 1) {
      setActiveAndRemember(accounts[0]);
    } else if (accounts.length > 1) {
      // Stale homeAccountId AND ambiguous cache. Don't guess; the template
      // will trigger interactive sign-in below and the LOGIN_SUCCESS event
      // handler will write the new pick to sessionStorage.
      sessionStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    }
  }

  // Keep the active account + sessionStorage in sync if MSAL fires
  // login/acquire events later (e.g. interactive sign-in completes,
  // `acquireTokenSilent` refreshes, another tab signs in via popup).
  msalInstance.addEventCallback((event: EventMessage) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload &&
      'account' in event.payload
    ) {
      const account = (event.payload as { account?: AccountInfo }).account;
      if (account) setActiveAndRemember(account);
    } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
      rememberActiveAccount(null);
    }
  });

  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

// Surface failures that happen INSIDE bootstrap() deterministically — MSAL
// initialize() / handleRedirectPromise() rejections, the "Root container
// missing" throw, and anything else thrown after `await msalInstance.…`
// starts running. Without this, an unhandled rejection just leaves the
// page blank, which in prod looks identical to a successful "still
// loading" state with no clue why.
//
// IMPORTANT — what this does NOT catch:
//   - Throws during ES module evaluation of `./auth/msalConfig` itself
//     (PublicClientApplication constructor failures, for instance). Those
//     happen BEFORE main.tsx's own code runs because msalConfig is statically
//     imported at the top of this file, so a Promise.catch in user-land
//     can't see them. The Entra identifiers in msalConfig.ts are now
//     source-level constants so the "missing env var" failure class no
//     longer exists — anything still thrown at evaluation time is a real
//     msal-browser surprise worth seeing in the console as-is.
//   - Bundle download / parse errors (the script never executes at all).
//
// Render a minimal inline fallback rather than pulling in a Fluent
// component — the failure mode may itself be the bundle being half-loaded.
function renderBootstrapFailure(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  console.error('[bootstrap] hub-web failed to initialize', error);
  const root = document.getElementById('root');
  if (root === null) return;
  // Inline styles only — global CSS may not be applied yet on early failures.
  root.innerHTML = `
    <div style="font-family: system-ui, sans-serif; max-width: 560px; margin: 80px auto; padding: 24px; line-height: 1.5;">
      <h1 style="font-size: 1.25rem; margin: 0 0 12px;">Hub couldn't start</h1>
      <p style="margin: 0 0 16px;">Reload the page. If the problem persists, check the browser console and share the error below with the team.</p>
      <pre style="background: #f5f5f5; color: #111; padding: 12px; border-radius: 4px; overflow: auto; white-space: pre-wrap; word-break: break-word;"></pre>
    </div>`;
  // Use textContent on the <pre> to avoid HTML injection from error text.
  const pre = root.querySelector('pre');
  if (pre !== null) pre.textContent = message;
}

bootstrap().catch(renderBootstrapFailure);
